import { useAssistantRuntime, useThread } from '@assistant-ui/react';
import { isDmaConsultantUser } from '@upright/common/utils';
import { Button } from '@upright/ui/common/generic/components/button';
import { Modal } from '@upright/ui/common/generic/components/modal/Modal';
import { Heading, Text } from '@upright/ui/common/generic/components/typography';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { CHAT_PATH, isChatPath } from '@upright/ui/views/unified-shell/unified-shell-chat-routes';
import { startNewChat } from './chat-history-actions';
import {
  type ChatConversationListItem,
  conversationDisplayTitle,
  useDeleteAllChatConversations,
  useDeleteChatConversation,
} from './chat-history-data';
import { useActiveConversationId } from './chat-history-hooks';
import { composerState } from '../upright-chat-shared';
import styles from './chat-delete-dialog.module.css';

export function ChatDeleteDialog({
  conversation,
  onClose,
}: {
  conversation: ChatConversationListItem | 'all' | null;
  onClose: () => void;
}) {
  const single = useDeleteChatConversation();
  const all = useDeleteAllChatConversations();
  const runtime = useAssistantRuntime();
  const isRunning = useThread(state => state.isRunning);
  const isConsultant = isDmaConsultantUser(useGetLoggedInUser());
  const activeId = useActiveConversationId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pending = single.isPending || all.isPending;
  const failed = single.isError || all.isError;

  // Whether a conversation is the one on screen. The active id is kept in step with each turn by
  // the `/chat` sidebar and the investor rail; on the pages under the chat side panel neither is
  // usually mounted, so a conversation started there is known by the thread's own id instead.
  function isOnScreen(id: string): boolean {
    return id === activeId || (id === composerState.threadId && runtime.thread.getState().messages.length > 0);
  }

  async function confirm() {
    if (conversation === null || pending || isRunning) return;
    try {
      if (conversation === 'all') await all.mutateAsync();
      else await single.mutateAsync(conversation.id);
      if (conversation === 'all' || isOnScreen(conversation.id)) {
        startNewChat({ runtime, isConsultant });
        if (isChatPath(pathname)) navigate(CHAT_PATH, { replace: true });
      }
      onClose();
    } catch {
      // The mutation retains its error so the dialog stays open and offers a retry.
    }
  }

  return (
    <Modal
      isOpen={conversation !== null}
      onClose={() => {
        if (!pending) onClose();
      }}
      aria-label="Confirm delete"
      maxInlineSize="24rem"
    >
      <div className={styles.body}>
        <Heading level={2} size="lg" weight="semibold">
          {conversation === 'all' ? 'Delete all chats?' : 'Delete conversation?'}
        </Heading>
        <Text>
          {conversation === 'all'
            ? 'Delete all of your chats, including older chats not shown in the list? This can’t be undone.'
            : `Delete “${conversation ? conversationDisplayTitle(conversation) : ''}”? This can’t be undone.`}
        </Text>
        {isRunning && <Text>Wait for the current response to finish before deleting chats.</Text>}
        {failed && <div role="alert">Couldn’t delete chats. Please try again.</div>}
        <div className={styles.actions}>
          <Button variant="outlined" onPress={onClose} isDisabled={pending}>
            Cancel
          </Button>
          <Button
            color="danger"
            onPress={() => {
              void confirm();
            }}
            isPending={pending}
            isDisabled={isRunning}
          >
            {conversation === 'all' ? 'Delete all chats' : 'Delete'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
